import { AlertTriangle, Loader2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useWhatsAppSignup } from "@/hooks/parametres/useWhatsAppSignup";
import { WhatsAppConnectionStatus } from "@/redux/services/integrations/whatsapp.type";
import { cn } from "@/lib/utils";

const StatusBadge = ({ status }: { status: WhatsAppConnectionStatus }) => {
  const tone =
    status === WhatsAppConnectionStatus.Connected
      ? "bg-green-100 text-green-700"
      : status === WhatsAppConnectionStatus.Pending
        ? "bg-amber-100 text-amber-700"
        : "bg-slate-100 text-slate-600";

  const label =
    status === WhatsAppConnectionStatus.Connected
      ? "Connecté"
      : status === WhatsAppConnectionStatus.Pending
        ? "En attente"
        : status === WhatsAppConnectionStatus.Error
          ? "Erreur"
          : "Non connecté";

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
        tone
      )}
    >
      {label}
    </span>
  );
};

const WhatsAppCard = () => {
  const {
    connection,
    isLoading,
    isConnecting,
    isDisconnecting,
    isConfigured,
    error,
    startSignup,
    disconnect,
  } = useWhatsAppSignup();

  const confirm = useConfirm();
  const status = connection?.status ?? WhatsAppConnectionStatus.Disconnected;
  const isConnected = status === WhatsAppConnectionStatus.Connected;
  const isPending = status === WhatsAppConnectionStatus.Pending;

  const handleDisconnect = async () => {
    const confirmed = await confirm({
      title: "Déconnecter WhatsApp ?",
      description:
        "Votre restaurant ne recevra plus de messages WhatsApp tant que le compte ne sera pas reconnecté.",
      confirmText: "Déconnecter",
      variant: "destructive",
    });
    if (confirmed) await disconnect();
  };

  return (
    <div className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-700">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-semibold text-card-foreground">WhatsApp</h4>
              {!isLoading && <StatusBadge status={status} />}
            </div>
            <p className="mt-1 max-w-prose text-sm text-muted-foreground">
              Connectez le compte WhatsApp Business de votre restaurant pour
              échanger avec vos clients depuis ScaniFood.
            </p>

            {isConnected && (
              <div className="mt-3 space-y-0.5 text-sm">
                {connection?.verifiedName && (
                  <p className="font-medium text-card-foreground">
                    {connection.verifiedName}
                  </p>
                )}
                {connection?.displayPhoneNumber && (
                  <p className="text-muted-foreground">
                    {connection.displayPhoneNumber}
                  </p>
                )}
              </div>
            )}

            {isPending && (
              <p className="mt-3 text-sm text-amber-700">
                Votre compte est en cours de validation. Cette étape peut
                prendre quelques minutes.
              </p>
            )}

            {!isConfigured && (
              <p className="mt-3 text-sm text-muted-foreground">
                L'intégration WhatsApp n'est pas encore disponible sur cet
                environnement. Contactez le support ScaniFood pour l'activer.
              </p>
            )}

            {error && (
              <p className="mt-3 flex items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="shrink-0">
          {isConnected ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? "Déconnexion…" : "Déconnecter"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={startSignup}
              disabled={isConnecting || isLoading}
            >
              {isConnecting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connexion…
                </span>
              ) : (
                "Connecter WhatsApp"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppCard;
